import { useEffect, useState } from "react";
import {
  bearingBetween,
  streetViewMetadataUrl,
} from "../Utils/googleMapsImageUrls";

/**
 * Reply from Google's Street View metadata endpoint. Discriminated by
 * `status`:
 *   - `null`         → still checking (don't render the slide yet)
 *   - `false`        → no outdoor panorama nearby, skip the slide
 *   - `{ ok: true }` → safe to render; use the enclosed `heading` to
 *     face the property, not whatever direction the pano's default
 *     orientation was (often a wall or the far side of a road).
 */
export type StreetViewAvailability =
  | null
  | false
  | { ok: true; heading: number };

interface AvailabilityState {
  lat?: number;
  lng?: number;
  status: StreetViewAvailability;
}

const INITIAL: AvailabilityState = {
  lat: undefined,
  lng: undefined,
  status: null,
};

/**
 * Ask Google whether an OUTDOOR Street View panorama exists near the
 * property, and if so, compute the compass heading from that pano
 * back toward the property so the camera actually points at the
 * house.
 *
 * The metadata endpoint is FREE. Using it before requesting the
 * (billed) image avoids two costs: (1) the API call itself, and (2)
 * the ugly "Sorry, we have no imagery here" placeholder Google would
 * otherwise return.
 *
 * The internal state pairs lat/lng with status — the hook only returns
 * a non-null answer when the stored answer is for the EXACT coords the
 * caller is asking about, so consumers never see a one-render flash of
 * stale data during a coord change.
 *
 * On unmount or coord change, the in-flight fetch is cancelled via
 * AbortController.
 */
export const useStreetViewAvailability = (
  lat?: number,
  lng?: number
): StreetViewAvailability => {
  const [state, setState] = useState<AvailabilityState>(INITIAL);

  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") {
      // Store the actual input pair so the coord-match check at render
      // time returns the cached `false` instead of falling through to null.
      setState({ lat, lng, status: false });
      return;
    }

    const controller = new AbortController();
    setState({ lat, lng, status: null });

    fetch(streetViewMetadataUrl(lat, lng), { signal: controller.signal })
      .then((r) => r.json())
      .then((j) => {
        if (controller.signal.aborted) return;
        if (j?.status !== "OK") {
          setState({ lat, lng, status: false });
          return;
        }
        // Compute heading from the returned pano location TOWARD the
        // property. When the pano is on the same tile as the property,
        // this is close to zero and doesn't matter; when the pano is a
        // few metres up the road, this rotates the camera so the house
        // is centred instead of showing the road.
        const panoLat = Number(j?.location?.lat);
        const panoLng = Number(j?.location?.lng);
        const heading =
          Number.isFinite(panoLat) && Number.isFinite(panoLng)
            ? bearingBetween(panoLat, panoLng, lat, lng)
            : 0;
        setState({ lat, lng, status: { ok: true, heading } });
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.name === "AbortError") return;
        // Network / parse failure → assume unavailable. Better to hide
        // a slide than show a broken one.
        setState({ lat, lng, status: false });
      });

    return () => {
      controller.abort();
    };
  }, [lat, lng]);

  // Only honour the stored answer when it matches the CURRENT coords the
  // caller is asking about — otherwise return null (still checking).
  if (state.lat === lat && state.lng === lng) return state.status;
  return null;
};
